import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ArrayHelper, DateHelper } from 'apps/idl/src/helpers';
import { ConfigData, IHttpOptions, UserData } from 'apps/idl/src/model';
import { ContactsService, PlatformService } from 'apps/idl/src/services/security';
import { WorkspaceService } from 'apps/idl/src/services/security/workspace.service';
import { EDeviceType } from 'apps/idl/src/anakin/features/shared/enums/EDeviceType';
import { Observable, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ETypeLecteur } from '../../../anakin/features/shared/enums/ETypeLecteur';
import { ITerminal } from '../models/iterminal';
import { ITerminalInfo } from '../models/iterminal-info';
import { AuthenticatedRequestOptionBuilder } from 'apps/idl/src/model/api/authenticated-request-option-builder';

@Injectable()
export class SceService {

	public erreurLectureCVSubject = new Subject<string>();
	public erreurLectureCV$: Observable<string> = this.erreurLectureCVSubject.asObservable();

	//#endregion

	//#region PROPERTIES



	//#endregion

	//#region METHODS

	constructor(
		private readonly ioHttpClient: HttpClient,
		private readonly isvcWorkspace: WorkspaceService,
		private readonly svcPlatform : PlatformService
	) { }


	private get baseUrl(): string {
		return `${ConfigData.environment.cloud_url}${ConfigData.environment.cloud_api_apps_suffix}`;
	}

	private get baseUrlLecteursSce(): string {
		return `${ConfigData.environment.cloud_url}${ConfigData.environment.cloud_api_apps_suffix}sce/lecteurs/`;
	}

	private get terminalsBaseUrl(): string {
		return `${this.baseUrl}workspaces/${this.isvcWorkspace.getWorkspaceNameFromId(ArrayHelper.getFirstElement(UserData.current.workspaceInfos).id)}/contacts/${ContactsService.getUserContactId()}/terminals`;
	}

	/** Retourne la liste des terminaux disponibles. */
	public listTerminals(numerosFacturationPs: string, numNatPs: string, desktopOnly: boolean = true): Observable<ITerminal[]> {
		const deviceTypeUser: EDeviceType = this.svcPlatform.getDeviceType();
		const urlUsers = `${this.baseUrlLecteursSce}utilisateurs`;
		const urlCenters = `${this.baseUrlLecteursSce}centres`;

		const doubledNumNatPs = this.doubleLastkeyNumNat(numNatPs);
		const bodyUsers = { numerosFacturationPs, doubledNumNatPs };
		const bodyCenters = { numerosFacturationPs };
		const options = AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions();

		return this.ioHttpClient.post<{ Lecteurs: any[] }>(urlUsers, bodyUsers, options).pipe(
			switchMap(userResponse => {
				const userLecteursInit = userResponse.Lecteurs;
				const userLecteurs = userResponse.Lecteurs.filter(lecteur => {
					const isCorrectType = lecteur.typeLecteur === ETypeLecteur.OLAQIN;
					const isSceAndMatchingDevice = lecteur.typeLecteur === ETypeLecteur.SCE && lecteur.typeDevice === deviceTypeUser
					return isCorrectType || isSceAndMatchingDevice;
				});
				const lastUseDates = userLecteurs.reduce<Record<string, string>>((acc, lecteur) => {
					if (lecteur.dateDerniereUtilisation) {
						acc[lecteur.identifiant] = lecteur.dateDerniereUtilisation;
					}
					return acc;
				}, {});
				const lastUsedTerminalId = Object.keys(lastUseDates).reduce((latestId, currentId) => {
					if (!latestId) return currentId;
					return new Date(lastUseDates[currentId]).getTime() > new Date(lastUseDates[latestId]).getTime()
						? currentId
						: latestId;
				}, '');
				return this.ioHttpClient.post<{ Lecteurs: any[] }>(urlCenters, bodyCenters, options).pipe(
					map(centerResponse =>
						centerResponse.Lecteurs
						.filter(lecteur => {
							if (!desktopOnly) {
								return lecteur.typeLecteur !== ETypeLecteur.OLAQIN;
							}

							if (this.svcPlatform.isAndroid) {
								return lecteur.typeDevice !== EDeviceType.IOS;
							}

							if (this.svcPlatform.isIOS) {
								return lecteur.typeDevice !== EDeviceType.ANDROID;
							}

							if (!this.svcPlatform.isMobileApp) {
								return lecteur.typeDevice !== EDeviceType.ANDROID && lecteur.typeDevice !== EDeviceType.IOS;
							}
						return true;
					})						
					.map(lecteur => ({
							terminalId: lecteur.identifiant,
							type: lecteur.typeLecteur,
							serialNumber: lecteur.identifiantNational || '',
							name: lecteur.nom,
							lastUsed: lecteur.identifiant === lastUsedTerminalId,
							device: lecteur.typeDevice,
							lastUsedDate: DateHelper.isDate(userLecteursInit.find(lec => lec.identifiant === lecteur.identifiant)?.dateDerniereUtilisation) ? new Date(userLecteursInit.find(lec => lec.identifiant === lecteur.identifiant).dateDerniereUtilisation) : null
						} as ITerminal)
					)
					)
				);
			})
		);
	}

	public getTerminalInfos(terminal: ITerminal): Observable<ITerminalInfo> {
		const loRequestParams: IHttpOptions = AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions();
		loRequestParams.params = {typeLecteur:terminal.type,systemId: "fsv"};
		return this.ioHttpClient.get<ITerminal>(`${this.terminalsBaseUrl}/${terminal.terminalId}`, loRequestParams);
	}

	private doubleLastkeyNumNat(str: string): string {
		if (str.length === 0) return str;
		return str.slice(0, -1) + str.charAt(str.length - 1).repeat(2);
	}

	public createLecteur(name: string, deviceType: EDeviceType): Observable<ITerminal> {
		const urlLecteur = `${this.baseUrlLecteursSce}sce`;
		const bodyLecteur = { nom: name, typeDevice: deviceType };
		const options = AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions();

		return this.ioHttpClient.post<any>(urlLecteur, bodyLecteur, options).pipe(
			map((term) => ({
				terminalId: term.identifiant,
				type: term.typeLecteur,
				name:term.nom,
				device: term.typeDevice
			} as ITerminal
		)))
	}

	public modifyLecteur(name: string, deviceId: string): Observable<ITerminal> {
		const urlLecteur = `${this.baseUrlLecteursSce}sce/${deviceId}`;
		const bodyLecteur = { nomLecteur: name };
		const options = AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions();

		return this.ioHttpClient.patch<any>(urlLecteur, bodyLecteur, options);
	}

	public supprimerLecteur(deviceId: string): Observable<ITerminal> {
		const urlLecteur = `${this.baseUrlLecteursSce}sce/${deviceId}`;
		const options = AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions();

		return this.ioHttpClient.delete<any>(urlLecteur, options);
	}

	public associerLecteurUF(deviceId: string, finess: string): Observable<any> {
		const urlLecteur = `${this.baseUrlLecteursSce}sce/${deviceId}/associer/${finess}`;
		const options = AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions();

		return this.ioHttpClient.put(urlLecteur, null, options)
	}

	public supprimerAssociationLecteurUF(deviceId: string, finess: string): Observable<any> {
		const urlLecteur = `${this.baseUrlLecteursSce}sce/${deviceId}/associer/${finess}`;
		const options = AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions();

		return this.ioHttpClient.delete(urlLecteur, options)
	}
	//#endregion
}
